import { useState } from 'react';
import { CheckCircle, AlertCircle, FileCheck } from 'lucide-react';

export interface ConsentFormData {
  accuracyAttestation: boolean;
  submissionAgreement: boolean;
  contactPermission: boolean;
}

interface ConsentFormProps {
  onConsentChange: (consent: ConsentFormData) => void;
  onValidChange: (isValid: boolean) => void;
}

export function ConsentForm({ onConsentChange, onValidChange }: ConsentFormProps) {
  const [consent, setConsent] = useState<ConsentFormData>({
    accuracyAttestation: false,
    submissionAgreement: false,
    contactPermission: false,
  });

  const handleChange = (field: keyof ConsentFormData) => {
    const newConsent = {
      ...consent,
      [field]: !consent[field],
    };
    setConsent(newConsent);
    onConsentChange(newConsent);
    
    // All required consents must be checked (contactPermission is optional)
    const isValid = newConsent.accuracyAttestation && newConsent.submissionAgreement;
    onValidChange(isValid);
  };

  const allRequired = consent.accuracyAttestation && consent.submissionAgreement;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-justice-navy/5 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <FileCheck className="h-5 w-5 text-justice-navy" />
          <h3 className="font-semibold text-justice-navy">Legal Consent & Acknowledgment</h3>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Please review and acknowledge the following before submitting evidence.
        </p>
      </div>

      <div className="p-6 space-y-4">
        {/* Accuracy Attestation - Required */}
        <label className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-justice-navy/30 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={consent.accuracyAttestation}
            onChange={() => handleChange('accuracyAttestation')}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-justice-navy focus:ring-justice-navy/20"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Accuracy Attestation</span>
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Required</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              I attest that the evidence I am submitting is accurate, unaltered, and represents 
              a true and complete record to the best of my knowledge.
            </p>
          </div>
          {consent.accuracyAttestation && (
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          )}
        </label>

        {/* Submission Agreement - Required */}
        <label className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-justice-navy/30 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={consent.submissionAgreement}
            onChange={() => handleChange('submissionAgreement')}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-justice-navy focus:ring-justice-navy/20"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Submission Agreement</span>
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Required</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              I understand that this evidence will become part of an official legal record and may 
              be used in judicial proceedings. I consent to the storage and processing of this 
              submission per court policy.
            </p>
          </div>
          {consent.submissionAgreement && (
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          )}
        </label>

        {/* Contact Permission - Optional */}
        <label className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-justice-navy/30 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={consent.contactPermission}
            onChange={() => handleChange('contactPermission')}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-justice-navy focus:ring-justice-navy/20"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Contact Permission</span>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Optional</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              I agree to be contacted by court staff if additional information is needed regarding 
              this submission. Check this box if you would like to receive status updates.
            </p>
          </div>
          {consent.contactPermission && (
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          )}
        </label>

        {/* Status indicator */}
        <div className={`flex items-center gap-2 pt-2 ${allRequired ? 'text-green-600' : 'text-amber-600'}`}>
          {allRequired ? (
            <>
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">All required consents acknowledged</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Please acknowledge required items to continue</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
